/*
 * ===========================================================================
 * [StrongGate Limited] [http://www.stronggate.com]
 * Copyright (c) 2026. All rights Reserved.
 * Unauthorized access, use, dissemination, distribution, reproduction of this
 * file, or a portion of, in any form is prohibited and unlawful. If you receive
 * this file, and you are not an authorized company employee, or you believe you
 * have received this file in error, please notify the sender (or originator)
 * and promptly delete this file.
 *
 * Project:
 * File: SafeHtmlDirective.ts
 * Created by: claude
 * Create Date: 2026-08-07
 * Version Number: 1.0
 * Version Description: v-safe-html — sanitized replacement for v-html.
 *
 * =============================================================================
 */

import type {App, DirectiveBinding} from 'vue';
import DOMPurify from 'dompurify';

/**
 * Sanitize an HTML fragment before it is written to the DOM.
 * Keeps standard formatting markup (span/b/br/style attribute, ...) and strips
 * script, event handlers, javascript: URLs and other XSS vectors.
 */
export function sanitizeHtml(html: unknown): string {
    if (html === null || html === undefined) {
        return '';
    }
    return DOMPurify.sanitize(String(html), {USE_PROFILES: {html: true}});
}

/**
 * `v-safe-html` — drop-in replacement for `v-html`. All HTML rendered through
 * this directive is sanitized with DOMPurify, so values that contain user- or
 * server-supplied content cannot inject executable markup.
 */
export default {
    install: (Vue: App) => {
        Vue.directive('safe-html', (el: HTMLElement, binding: DirectiveBinding) => {
            if (binding.value === binding.oldValue) return;
            el.innerHTML = sanitizeHtml(binding.value);
        });
    }
};
